import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Plus, Trash2, Filter, Pencil, Check, X as XIcon } from "lucide-react";

interface TargetingRule {
  id: number;
  ruleType: string;
  targetingAction: "include" | "exclude";
  values: string[];
  createdAt: string;
}

const RULE_TYPES = [
  { value: "country", label: "Country" },
  { value: "platform", label: "Platform" },
  { value: "device", label: "Device Type" },
  { value: "os", label: "Operating System" },
  { value: "browser", label: "Browser" },
  { value: "language", label: "Language" },
  { value: "region", label: "Region" },
  { value: "city", label: "City" },
  { value: "isp", label: "ISP" },
  { value: "ip", label: "IP Address" },
  { value: "proxy_blocking", label: "Proxy Blocking" },
];

export function OfferTargetingTab({ offerId }: { offerId: string }) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editAction, setEditAction] = useState<"include" | "exclude">("include");
  const [editValues, setEditValues] = useState("");

  const updateMutation = useMutation({
    mutationFn: async ({ ruleId, data }: { ruleId: number; data: Record<string, unknown> }) => {
      const res = await api.patch(`/v1/offers/${offerId}/targeting/${ruleId}`, data);
      if (!res.ok) throw new Error("Failed to update rule");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "targeting"] });
      setEditingId(null);
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ["offers", offerId, "targeting"],
    queryFn: async () => {
      const res = await api.get(`/v1/offers/${offerId}/targeting`);
      if (!res.ok) throw new Error("Failed to fetch targeting rules");
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (ruleId: number) => {
      const res = await api.delete(`/v1/offers/${offerId}/targeting/${ruleId}`);
      if (!res.ok) throw new Error("Failed to delete rule");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["offers", offerId, "targeting"] }),
  });

  const rules: TargetingRule[] = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-700">
          {rules.length} targeting rule{rules.length !== 1 ? "s" : ""}
        </h3>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-800"
        >
          <Plus className="w-4 h-4" /> Add Rule
        </button>
      </div>

      {isLoading ? (
        <div className="h-32 bg-gray-100 rounded-xl animate-pulse" />
      ) : rules.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-xl border border-gray-200">
          <Filter className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No targeting rules configured.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map((rule) => (
            <div key={rule.id} className="bg-white rounded-xl border border-gray-200 p-4">
              {editingId === rule.id ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-medium text-gray-500 uppercase px-2 py-1 bg-gray-100 rounded">
                      {RULE_TYPES.find((t) => t.value === rule.ruleType)?.label || rule.ruleType}
                    </span>
                    <select value={editAction} onChange={(e) => setEditAction(e.target.value as "include" | "exclude")} className="text-xs rounded-lg border border-gray-300 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-gray-900">
                      <option value="include">Include</option>
                      <option value="exclude">Exclude</option>
                    </select>
                  </div>
                  <textarea value={editValues} onChange={(e) => setEditValues(e.target.value)} rows={4} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-gray-900" placeholder="One value per line" />
                  <div className="flex items-center gap-2 justify-end">
                    <button onClick={() => setEditingId(null)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded"><XIcon className="w-4 h-4" /></button>
                    <button onClick={() => updateMutation.mutate({ ruleId: rule.id, data: { targetingAction: editAction, values: editValues.split("\n").map((v: string) => v.trim()).filter(Boolean) } })} disabled={updateMutation.isPending} className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
                      <Check className="w-3.5 h-3.5" /> Save
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-medium text-gray-500 uppercase px-2 py-1 bg-gray-100 rounded">
                        {RULE_TYPES.find((t) => t.value === rule.ruleType)?.label || rule.ruleType}
                      </span>
                      <span className={`text-xs font-medium px-2 py-1 rounded ${rule.targetingAction === "include" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
                        {rule.targetingAction === "include" ? "Include" : "Exclude"}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => { setEditingId(rule.id); setEditAction(rule.targetingAction); setEditValues(Array.isArray(rule.values) ? rule.values.join("\n") : String(rule.values)); }} className="p-1.5 text-gray-400 hover:text-gray-600 rounded transition-colors">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => { if (window.confirm("Delete this targeting rule?")) deleteMutation.mutate(rule.id); }} className="p-1.5 text-red-400 hover:text-red-600 rounded transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 mb-1">Values:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(Array.isArray(rule.values) ? rule.values : []).map((value: string, idx: number) => (
                        <span key={idx} className="text-xs bg-gray-50 text-gray-700 px-2 py-1 rounded border border-gray-200">{value}</span>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {showForm && <TargetingRuleFormModal offerId={offerId} onClose={() => setShowForm(false)} />}
    </div>
  );
}

function TargetingRuleFormModal({ offerId, onClose }: { offerId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [ruleType, setRuleType] = useState("");
  const [targetingAction, setTargetingAction] = useState<"include" | "exclude">("include");
  const [values, setValues] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const valuesArray = values
        .split("\n")
        .map((v) => v.trim())
        .filter((v) => v.length > 0);

      if (valuesArray.length === 0) {
        throw new Error("At least one value is required");
      }

      const res = await api.post(`/v1/offers/${offerId}/targeting`, {
        ruleType,
        targetingAction,
        values: valuesArray,
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error || "Failed to create rule");
      }
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "targeting"] });
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6">
        <h2 className="text-lg font-semibold mb-4">Add Targeting Rule</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">{error}</div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rule Type *</label>
            <select
              required
              value={ruleType}
              onChange={(e) => setRuleType(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              <option value="">Select rule type...</option>
              {RULE_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Action *</label>
            <select
              required
              value={targetingAction}
              onChange={(e) => setTargetingAction(e.target.value as "include" | "exclude")}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              <option value="include">Include</option>
              <option value="exclude">Exclude</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Values *</label>
            <textarea
              required
              value={values}
              onChange={(e) => setValues(e.target.value)}
              rows={6}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 font-mono"
              placeholder="Enter one value per line&#10;e.g.,&#10;US&#10;CA&#10;GB"
            />
            <p className="text-xs text-gray-400 mt-1">Enter one value per line</p>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
